import { resolveGapiProxyOrigin } from './gapiProxy';

/** Cuba 2G/3G: 7s false-unhealthy. Blocked hosts still fail in milliseconds. */
const PROBE_TIMEOUT_MS = 12_000;

async function fetchWithTimeout(url: string, init: RequestInit = {}): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    // Defaults first so callers can override `mode` (Google probe needs `no-cors`).
    return await fetch(url, {
      cache: 'no-store',
      credentials: 'omit',
      mode: 'cors',
      ...init,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Cheap Google reachability probe.
 * Uses `no-cors` so opaque success still means the host is reachable (CORS is irrelevant).
 */
export async function probeGoogleApis(): Promise<boolean> {
  try {
    await fetchWithTimeout('https://www.googleapis.com/generate_204', {
      method: 'GET',
      mode: 'no-cors',
      redirect: 'follow',
    });
    return true;
  } catch {
    try {
      await fetchWithTimeout('https://firestore.googleapis.com/', {
        method: 'GET',
        mode: 'no-cors',
        redirect: 'follow',
      });
      return true;
    } catch {
      return false;
    }
  }
}

export async function probeGapiProxy(proxyOrigin: string): Promise<boolean> {
  const healthUrl = `${proxyOrigin.replace(/\/$/, '')}/gapi/health`;
  try {
    const res = await fetchWithTimeout(healthUrl, { method: 'GET' });
    if (!res.ok) return false;
    const body = (await res.json()) as { ok?: boolean };
    return body?.ok === true;
  } catch {
    return false;
  }
}

/** Probe Hosting candidates in priority order; first healthy wins. */
export async function pickHealthyHostingOrigin(
  candidates: string[],
): Promise<{ origin: string; reachable: boolean }> {
  const unique = [...new Set(candidates.map((o) => o.replace(/\/$/, '')).filter(Boolean))];
  if (unique.length === 0) {
    return { origin: resolveGapiProxyOrigin(), reachable: false };
  }
  return await new Promise((resolve) => {
    let pending = unique.length;
    let settled = false;
    const reachability = new Map<string, boolean>();
    const finish = (value: { origin: string; reachable: boolean }) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    for (const origin of unique) {
      void probeGapiProxy(origin).then((reachable) => {
        reachability.set(origin, reachable);
        pending -= 1;
        for (const candidate of unique) {
          if (!reachability.has(candidate)) break;
          if (reachability.get(candidate)) {
            finish({ origin: candidate, reachable: true });
            return;
          }
        }
        if (pending === 0) finish({ origin: unique[0]!, reachable: false });
      });
    }
  });
}

export async function isNativeCapacitor(): Promise<boolean> {
  try {
    const { Capacitor } = await import('@capacitor/core');
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/** Sync read of the Capacitor bridge (APK/iOS WebView) without awaiting the SDK. */
export function isNativeCapacitorSync(): boolean {
  if (typeof window === 'undefined') return false;
  const cap = (window as Window & {
    Capacitor?: { isNativePlatform?: () => boolean };
  }).Capacitor;
  return cap?.isNativePlatform?.() === true;
}
